/**
 * Custom Line Items - Helper Products Manager
 * -------------------------------------------
 * Automatically adds embroidery, personalization, and single-product upcharge
 * helper products to cart based on logo/personalization counts and item price.
 *
 * Reads helper product handles from window.helperProducts (set by storefront metaobject).
 */

(function() {
  'use strict';

  const LOG_PREFIX = '[CustomLineItems]';

  let isUpdating = false;
  let pendingSync = false;

  // Cache variant IDs after first fetch so we don't re-fetch every cart event
  let variantIdCache = null;

  // Upcharge product variants can't be pre-cached (price-dependent), but we
  // can cache the product itself to avoid re-fetching on every sync.
  let upchargeProductCache = null;

  function getHelperHandles() {
    const hp = window.helperProducts || {};
    if (!hp.embroidery || !hp.additionalLogos || !hp.personalization) {
      console.warn(LOG_PREFIX, 'Missing helper product handles in window.helperProducts:', hp);
      return null;
    }
    // upcharge is optional — storefronts without it simply skip that logic
    return hp;
  }

  function init() {
    const handles = getHelperHandles();
    if (!handles) {
      console.warn(LOG_PREFIX, 'Disabled — helper products not configured for this storefront');
      return;
    }

    console.log(LOG_PREFIX, 'Initialized with handles:', handles);

    document.addEventListener('ajaxProduct:added', function(e) {
      // Ignore events we dispatched ourselves (cart drawer refresh)
      if (e.detail?.source === 'customLineItems') return;
      handleCartUpdate();
    });
    document.addEventListener('cart:updated', handleCartUpdate);
    document.addEventListener('cart:update', handleCartUpdate);
    document.addEventListener('theme:cart:reload', () => {
      setTimeout(handleCartUpdate, 500);
    });
  }

  function handleCartUpdate() {
    if (isUpdating) {
      pendingSync = true;
      console.log(LOG_PREFIX, 'Sync in progress — queued follow-up');
      return;
    }
    runSync();
  }

  function runSync() {
    isUpdating = true;
    pendingSync = false;

    syncHelperProducts()
      .catch(err => console.error(LOG_PREFIX, 'Update FAILED:', err))
      .finally(() => {
        setTimeout(() => {
          isUpdating = false;
          if (pendingSync) {
            console.log(LOG_PREFIX, 'Running queued follow-up sync');
            handleCartUpdate();
          }
        }, 1000);
      });
  }

  /**
   * Fetch a variant ID from a product by SKU or first variant.
   */
  async function getVariantId(handle, sku) {
    const response = await fetch('/products/' + handle + '.js');
    if (!response.ok) throw new Error('Failed to fetch product ' + handle + ': ' + response.status);
    const product = await response.json();

    if (sku) {
      const variant = product.variants.find(v => v.sku === sku);
      return variant ? variant.id : null;
    }
    return product.variants[0]?.id || null;
  }

  /**
   * Fetch and cache all embroidery/additional/personalization variant IDs.
   * Only fetches once per page load.
   */
  async function getHelperVariantIds(handles) {
    if (variantIdCache) return variantIdCache;

    const [emb1, emb2, emb3, additionalId, personalizationId] = await Promise.all([
      getVariantId(handles.embroidery, 'EMB-1'),
      getVariantId(handles.embroidery, 'EMB-2'),
      getVariantId(handles.embroidery, 'EMB-3'),
      getVariantId(handles.additionalLogos),
      getVariantId(handles.personalization)
    ]);

    variantIdCache = { emb1, emb2, emb3, additionalId, personalizationId };
    console.log(LOG_PREFIX, 'Cached variant IDs:', variantIdCache);
    return variantIdCache;
  }

  /**
   * Fetch the upcharge product and cache it for the page lifetime.
   * The specific variant to use depends on price at runtime, so we
   * cache the full product and search variants dynamically each sync.
   */
  async function getUpchargeProduct(handle) {
    if (upchargeProductCache) return upchargeProductCache;
    const response = await fetch('/products/' + handle + '.js');
    if (!response.ok) {
      console.warn(LOG_PREFIX, 'Could not fetch upcharge product:', handle);
      return null;
    }
    upchargeProductCache = await response.json();
    console.log(LOG_PREFIX, 'Cached upcharge product:', upchargeProductCache.handle);
    return upchargeProductCache;
  }

  /**
   * Parse _customizations from cart attributes.
   */
  function parseCustomizations(cart) {
    const raw = cart.attributes?._customizations;
    if (!raw) return {};
    try {
      return typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch (e) {
      console.warn(LOG_PREFIX, 'Failed to parse _customizations:', e.message);
      return {};
    }
  }

  /**
   * Count logos and personalizations for a single cart item.
   */
  function countItemDecorations(item, customizations) {
    let logoCount = 0;
    let personalizationCount = 0;

    // Use the exact _customization_key from line item properties (source of truth)
    const custKey = item.properties?._customization_key;
    if (custKey && customizations[custKey]) {
      const data = customizations[custKey];
      if (data.locations && Array.isArray(data.locations)) {
        data.locations.forEach(loc => {
          if (loc.logo?.id) logoCount++;
          if (loc.personalization?.text?.trim()) personalizationCount++;
        });
      }
      return { logoCount, personalizationCount, source: '_customizations' };
    }

    // Fallback: line item properties (legacy items)
    const properties = item.properties;
    if (!properties || Object.keys(properties).length === 0) {
      return { logoCount: 0, personalizationCount: 0, source: 'none' };
    }

    for (const [key, value] of Object.entries(properties)) {
      const keyLower = key.toLowerCase();
      if ((keyLower === 'logo' || /^logo\s+\d+$/.test(keyLower)) && value?.toString().trim()) {
        logoCount++;
      }
      if (keyLower.startsWith('_personalization') && keyLower.includes('coordinates') && value?.toString().trim()) {
        personalizationCount++;
      }
    }

    return { logoCount, personalizationCount, source: 'line item properties' };
  }

  /**
   * Determine the upcharge variant ID and quantity for single-item orders.
   *
   * Rules:
   *   - Only applies when there is exactly one non-helper item with quantity = 1
   *   - Upcharge = 12.5% of item's final_line_price (in cents), converted to dollars
   *   - Variant on the upcharge product must have a title matching "$N" (e.g. "$14")
   *   - Returns { variantId, quantity } or null if condition not met / variant not found
   */
  async function resolveUpcharge(cart, helperHandles, upchargeHandle) {
    if (!upchargeHandle) return null;

    const validItems = cart.items.filter(item => !helperHandles.has(item.handle));

    if (validItems.length !== 1 || validItems[0].quantity !== 1) {
      console.log(LOG_PREFIX, 'Upcharge: condition not met — validItems:', validItems.length,
        validItems.length === 1 ? ', qty: ' + validItems[0].quantity : '');
      return null;
    }

    const item = validItems[0];
    const priceCents = item.final_line_price;
    const upchargeAmount = Math.floor((priceCents * 0.125) / 100); // 12.5%, convert cents → dollars

    console.log(LOG_PREFIX, `Upcharge: single item "${item.title}", price $${priceCents / 100}, upcharge $${upchargeAmount}`);

    const product = await getUpchargeProduct(upchargeHandle);
    if (!product) return null;

    const variant = product.variants.find(v => v.title === `$${upchargeAmount}`);
    if (!variant) {
      console.warn(LOG_PREFIX, `Upcharge: no variant found for $${upchargeAmount} on "${upchargeHandle}"`);
      return null;
    }

    return { variantId: variant.id, quantity: 1 };
  }

  /**
   * Main sync: read cart, count decorations, update all helper product quantities.
   */
  async function syncHelperProducts() {
    const handles = getHelperHandles();
    if (!handles) return;

    const helperHandles = new Set([
      handles.embroidery,
      handles.additionalLogos,
      handles.personalization,
      ...(handles.upcharge ? [handles.upcharge] : [])
    ]);

    const cartRoot = window.Shopify?.routes?.root ?? '/';
    const cart = await (await fetch(cartRoot + 'cart.js')).json();
    const customizations = parseCustomizations(cart);

    let firstLogoTotal = 0;
    let additionalLogoTotal = 0;
    let personalizationTotal = 0;

    for (const item of cart.items) {
      if (helperHandles.has(item.handle)) continue;

      const qty = item.quantity;
      const { logoCount, personalizationCount, source } = countItemDecorations(item, customizations);

      if (logoCount > 0) {
        firstLogoTotal += qty;
        if (logoCount > 1) additionalLogoTotal += qty * (logoCount - 1);
      }
      if (personalizationCount > 0) {
        personalizationTotal += qty * personalizationCount;
      }

      if (logoCount || personalizationCount) {
        console.log(LOG_PREFIX, `"${item.title}" (qty: ${qty}) → logos: ${logoCount}, personalization: ${personalizationCount} [${source}]`);
      }
    }

    console.log(LOG_PREFIX, `Totals → first logo: ${firstLogoTotal}, additional: ${additionalLogoTotal}, personalization: ${personalizationTotal}`);

    const ids = await getHelperVariantIds(handles);

    // Tiered embroidery pricing
    let emb1Qty = 0, emb2Qty = 0, emb3Qty = 0;
    if (firstLogoTotal >= 72 && ids.emb3) {
      emb3Qty = firstLogoTotal;
    } else if (firstLogoTotal >= 2 && ids.emb2) {
      emb2Qty = firstLogoTotal;
    } else {
      emb1Qty = firstLogoTotal;
    }

    // Build update payload for decoration helpers
    const updates = {};
    if (ids.emb1) updates[ids.emb1] = emb1Qty;
    if (ids.emb2) updates[ids.emb2] = emb2Qty;
    if (ids.emb3) updates[ids.emb3] = emb3Qty;
    if (ids.additionalId) updates[ids.additionalId] = additionalLogoTotal;
    if (ids.personalizationId) updates[ids.personalizationId] = personalizationTotal;

    // ── Upcharge ──────────────────────────────────────────────────────────────
    if (handles.upcharge) {
      const upchargeResolution = await resolveUpcharge(cart, helperHandles, handles.upcharge);

      // Find any currently-in-cart upcharge variant so we can zero it if needed
      const existingUpchargeItem = cart.items.find(i => i.handle === handles.upcharge);

      if (upchargeResolution) {
        // Add/update the correct upcharge variant
        updates[upchargeResolution.variantId] = upchargeResolution.quantity;

        // If there's a different upcharge variant already in cart, remove it
        if (existingUpchargeItem && existingUpchargeItem.variant_id !== upchargeResolution.variantId) {
          updates[existingUpchargeItem.variant_id] = 0;
          console.log(LOG_PREFIX, 'Upcharge: removing stale variant', existingUpchargeItem.variant_id);
        }

        console.log(LOG_PREFIX, 'Upcharge: setting variant', upchargeResolution.variantId, '→ qty 1');
      } else if (existingUpchargeItem) {
        // Condition no longer met — remove whatever upcharge is in cart
        updates[existingUpchargeItem.variant_id] = 0;
        console.log(LOG_PREFIX, 'Upcharge: removing — condition not met');
      }
    }
    // ── End Upcharge ──────────────────────────────────────────────────────────

    if (Object.keys(updates).length === 0) return;

    // Check if anything actually changed vs current cart
    const currentQuantities = {};
    for (const item of cart.items) {
      if (helperHandles.has(item.handle)) {
        currentQuantities[item.variant_id] = item.quantity;
      }
    }
    const hasChanges = Object.entries(updates).some(
      ([id, qty]) => (currentQuantities[id] || 0) !== qty
    );
    if (!hasChanges) {
      console.log(LOG_PREFIX, 'No changes needed — skipping cart update');
      return;
    }

    console.log(LOG_PREFIX, 'Updating cart:', updates);

    const updateRes = await fetch(cartRoot + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    });

    if (!updateRes.ok) {
      console.error(LOG_PREFIX, 'Cart update failed — status:', updateRes.status);
      return;
    }

    const updatedCart = await updateRes.json();
    console.log(LOG_PREFIX, 'Cart updated — item_count:', updatedCart.item_count);

    // Refresh cart drawer
    setTimeout(() => {
      document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
        detail: { source: 'customLineItems' }
      }));
    }, 100);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
